import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { CompleteTyresSearch } from "modules/IncompleteTyresDashboard/components/EditTyreModal/components/CompleteTyresSearch";
import { getTyreSeasonOptions } from "modules/IncompleteTyresDashboard/util";
import { EAN_VALIDATION, isKnownManufacturer } from "utils/common";

import type { DropdownItemProps } from "components";
import type { Tyre } from "models";

type EditTyreFormProps = {
  brandOptions: DropdownItemProps<string>[];
  isPending: boolean;
  onTyreReplace: (tyre: Tyre) => void;
  onClose: () => void;
};

export type EditTyreFormValues = Pick<Tyre, "id" | "manufacturer" | "description" | "carry_capacity" | "width" | "height" | "speed" | "season" | "run_flat_tire"> & {
  ean: string;
  size: string;
};

const FormHelper = createFormHelper<EditTyreFormValues>();

export const EditTyreForm = ({ brandOptions, isPending, onTyreReplace, onClose }: EditTyreFormProps) => {
  const { t } = useTranslation();
  const { watch, setValue, formState } = FormHelper.useFormContext();

  const [manufacturer, description, width, size, speed, height, ean, carryCapacity] = watch([
    "manufacturer",
    "description",
    "width",
    "size",
    "speed",
    "height",
    "ean",
    "carry_capacity"
  ]);

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-title">{t("v8_edit_tyre", "Edit tyre")}</div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && (!manufacturer || !isKnownManufacturer(brandOptions, manufacturer))}>
              <label>{t("v8_manufacturer", "Manufacturer")}</label>
              <FormHelper.Field.Dropdown
                name="manufacturer"
                options={brandOptions}
                dropdownProps={{ placeholder: t("v8_manufacturer", "Manufacturer"), fluid: true, selection: true, search: true, selectOnBlur: false }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !description}>
              <label>{t("v8_description", "Description")}</label>
              <FormHelper.Field.Input
                name="description"
                onChange={value => setValue("description", value.toUpperCase())}
                inputProps={{ placeholder: t("v8_description", "Description"), fluid: true }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !width}>
              <label>{t("v8_width", "Width")}</label>
              <FormHelper.Field.Input name="width" inputProps={{ placeholder: t("v8_width", "Width"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !(parseFloat(size) > 0)}>
              <label>{t("v8_size", "Size")}</label>
              <FormHelper.Field.Input name="size" inputProps={{ placeholder: t("v8_size", "Size"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !speed}>
              <label>{t("v8_speed", "Speed")}</label>
              <FormHelper.Field.Input
                name="speed"
                onChange={value => setValue("speed", value.toUpperCase())}
                inputProps={{ placeholder: t("v8_speed", "Speed"), fluid: true }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !height}>
              <label>{t("v8_height", "Height")}</label>
              <FormHelper.Field.Input name="height" inputProps={{ placeholder: t("v8_height", "Height"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>

          <GridColumn width={5}>
            <Form.Field required error={formState.isSubmitted && !EAN_VALIDATION.test(ean)}>
              <label>EAN</label>
              <FormHelper.Field.Input name="ean" onChange={value => setValue("ean", value.toUpperCase())} inputProps={{ placeholder: "EAN", fluid: true }} />
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !carryCapacity}>
              <label style={{ whiteSpace: "nowrap" }}>{t("v8_carry_capacity", "Carry capacity")}</label>
              <FormHelper.Field.Input name="carry_capacity" inputProps={{ placeholder: t("v8_carry_capacity", "Carry capacity"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_season", "Season")}</label>
              <FormHelper.Field.Dropdown name="season" options={getTyreSeasonOptions(t)} dropdownProps={{ fluid: true, selection: true, selectOnBlur: false }} />
            </Form.Field>
          </GridColumn>
          <GridColumn verticalAlign="middle">
            <FormHelper.Field.Checkbox name="run_flat_tire" label={t("v8_run_flat_tire", "Run flat tire")} />
          </GridColumn>
        </Grid>

        <CompleteTyresSearch onSelectReplacement={onTyreReplace} />
      </Modal.Content>

      <Modal.Actions className="padding-container">
        <div className="actions-container">
          <div className="save-cancel-buttons-container">
            <Form.Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
